import { useEffect, useMemo, useRef, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router'
import {
  clearLocalTokens,
  clearSessionTokens,
  isAuthenticated,
  setIsAuthenticated,
  setIsSuperUser,
  setTokens
} from 'utils/localStorageService.ts'
import { routes } from 'types/routes.ts'
import { checkIsSales } from 'utils/sales.ts'
import { ProtectedRouteProps } from 'router/protected-route/types.ts'
import { useStoreSelector } from 'hooks/store.ts'
import { getUserTypeLoginPage } from 'utils/user.ts'
import PageLoading from 'components/page-loading/PageLoading.tsx'
import boltBackend from 'lib/bolt-backend'
import { UserTypes } from 'store/auth/types.ts'
import { setUserType } from 'utils/userType.ts'
import { STORAGE_TYPES } from 'types/auth.ts'
import { logout } from 'utils/app.ts'
import router from 'router/routing.tsx'
import { MAGIC_TOKEN_QUERY_PARAM } from 'constants/auth.ts'

const ProtectedRoute = ({ userTypes }: ProtectedRouteProps) => {
  // Redux
  const { userType } = useStoreSelector((state) => state.auth)

  // Router
  const navigate = useNavigate()
  const { search } = useLocation()
  const query = useMemo<URLSearchParams>(() => new URLSearchParams(search), [search])
  const magicToken = query.get(MAGIC_TOKEN_QUERY_PARAM)

  // Local state
  const [authorized, setAuthorized] = useState<boolean>(false)

  // Tracks the magic token that has already been handed to the exchange, so it's only exchanged once.
  const handledMagicToken = useRef<string | null>(null)

  /**
   * Checks if the user is authenticated, if not, redirects to the login page
   */
  useEffect(() => {
    let ignore = false

    const authenticatedUserTypes = isAuthenticated()

    const handleMagicToken = async (token: string) => {
      const magicTokenUserType = UserTypes.CUSTOMER

      // Clear any leftover (possibly expired) magic-link session first so it can't interfere with requests made while the exchange is in flight.
      clearSessionTokens(magicTokenUserType)

      const tokens = await boltBackend.exchangeMagicTokens(token)
      if (tokens) {
        // Clear the local storage first
        clearLocalTokens(magicTokenUserType)

        // Set the new tokens
        setTokens(tokens, magicTokenUserType, STORAGE_TYPES.sessionStorage)
        setIsAuthenticated(true, magicTokenUserType)
        setUserType(magicTokenUserType)
        setIsSuperUser(false)

        // Remove magicToken from the url without re-render
        const cleanSearch = new URLSearchParams(search)
        cleanSearch.delete(MAGIC_TOKEN_QUERY_PARAM)
        void navigate({ search: cleanSearch.toString() }, { replace: true })

        // Set authenticated to true
        setAuthorized(true)
      } else {
        logout(() => void router.navigate(getUserTypeLoginPage(magicTokenUserType)), magicTokenUserType)
      }
    }

    if (!ignore) {
      if (magicToken && userTypes.includes(UserTypes.CUSTOMER)) {
        if (handledMagicToken.current !== magicToken) {
          handledMagicToken.current = magicToken
          void handleMagicToken(magicToken)
        }
      } else if (userTypes.every((userType) => !authenticatedUserTypes[userType])) {
        void navigate(checkIsSales() ? routes.LOGIN_SALES : getUserTypeLoginPage(userType))
      } else {
        setAuthorized(true)
      }
    }

    return () => {
      ignore = true
    }
  }, [navigate, userType, userTypes, magicToken, search])

  return authorized ? <Outlet /> : <PageLoading isLoading />
}

export default ProtectedRoute
